import { UpgradeBranch } from './UpgradePath';
import {
  FORTRESS_ABILITY_ID,
  FORTRESS_HP_BONUS,
  GRANDMASTER_ABILITY_ID,
  GRANDMASTER_MAX_HP,
} from './AbilityConstants';

export interface Minion {
  instanceId: string;
  baseMinionId: string;
  currentHp: number;
  attack: number;
  /** The minion's type classification (e.g., "warrior", "mage"). */
  minionType: string;
  upgradeCount: number;
  abilities: string[]; // Array of ability IDs
  upgradePathState: string; // Current position in the upgrade tree
  battlefieldPosition: number;
}

export class MinionInstance implements Minion {
  public instanceId: string;
  public baseMinionId: string;
  public currentHp: number;
  public attack: number;
  public minionType: string;
  public upgradeCount: number;
  public abilities: string[];
  public upgradePathState: string;
  public battlefieldPosition: number;
  /**
   * INFESTED (spell) marker: true while this minion carries a TEMPORARY
   * SwarmLord keyword granted by the Infested spell (the spell adds the
   * SWARMLORD ability directly — this flag only marks its ORIGIN, so the
   * post-combat removal never strips a class-granted SwarmLord). Cleared
   * by the RoundCoordinator's post-combat keyword removal (the one-combat
   * keyword — the minion keeps its body, loses the keyword).
   */
  public infestedSwarmLord: boolean;
  /**
   * REPEATER (Spellmaker, level 3 — task 184): the last minion-targeting
   * spell (its spell id, e.g. 'upgraded') that was cast ON THIS minion —
   * null if none yet. Set by PlayerState.castSpell for every minion-target
   * spell (and by the RoundCoordinator's Repeater pass when it re-casts on
   * a neighbor). At the start of combat a Repeater holder re-casts this
   * spell on both of its neighbors. Battlefield spells are never recorded
   * (they have no minion target to be cast "on").
   */
  public lastCastSpellId: string | null;
  /**
   * ENRICHMENT (spell, task 197): the per-card turn counter — the number
   * of NEW preparation turns this card has sat in the hand (0 when drawn;
   * the RoundCoordinator increments it at every round start for every
   * Enrichment card in the hand). The cast pays ENRICHMENT_BASE_GOLD +
   * this counter. Each Enrichment card carries its OWN counter (two cards
   * can value 2 and 4 gold at the same time). Spells are consumed on the
   * cast, so the counter dies with the card.
   */
  public enrichmentTurns: number;

  constructor(instanceId: string, baseMinionId: string, initialHp: number, initialAttack: number, upgradeCount: number = 0, upgradePathState: string = 'base', initialAbilities: string[] = [], minionType: string = 'warrior') {
    this.instanceId = instanceId;
    this.baseMinionId = baseMinionId;
    this.currentHp = initialHp;
    this.attack = initialAttack;
    this.minionType = minionType;
    this.upgradeCount = upgradeCount;
    this.abilities = [...initialAbilities]; // copy to avoid shared reference
    this.upgradePathState = upgradePathState;
    this.battlefieldPosition = -1; // -1 indicates not on battlefield
    this.infestedSwarmLord = false;
    this.lastCastSpellId = null;
    this.enrichmentTurns = 0;
  }

  public takeDamage(amount: number): void {
    this.currentHp -= amount;
    if (this.currentHp < 0) {
      this.currentHp = 0;
    }
  }

  public heal(amount: number): void {
    this.currentHp += amount;
  }

  /**
   * True when this minion carries the Grandmaster ability.
   */
  public hasGrandmaster(): boolean {
    return this.abilities.includes(GRANDMASTER_ABILITY_ID);
  }

  /**
   * Applies an HP gain to this minion, respecting the Grandmaster cap:
   * a minion that carries the Grandmaster ability can NEVER have more
   * than GRANDMASTER_MAX_HP (1) HP — any gain that would push it above
   * the cap is ignored. Non-Grandmaster minions behave exactly like a
   * plain `currentHp += amount`.
   */
  public gainHp(amount: number): void {
    this.currentHp += amount;
    if (this.hasGrandmaster() && this.currentHp > GRANDMASTER_MAX_HP) {
      this.currentHp = GRANDMASTER_MAX_HP;
    }
  }

  public setBattlefieldPosition(position: number): void {
    this.battlefieldPosition = position;
  }

  public clearBattlefieldPosition(): void {
    this.battlefieldPosition = -1;
  }

  public getLevel(): number {
    return this.upgradeCount + 1;
  }

  /**
   * Applies an upgrade branch to this minion, modifying its stats and state.
   * @param branch The upgrade branch to apply.
   */
  public applyUpgrade(branch: UpgradeBranch): void {
    this.currentHp += branch.hpChange;
    this.attack += branch.attackChange;
    this.upgradeCount += 1;
    this.upgradePathState = branch.nextPathState;

    if (branch.abilitiesAdded && branch.abilitiesAdded.length > 0) {
      const hadFortress = this.abilities.includes(FORTRESS_ABILITY_ID);
      for (const abilityId of branch.abilitiesAdded) {
        if (!this.abilities.includes(abilityId)) {
          this.abilities.push(abilityId);
        }
      }

      // Fortress: the +15 HP is gained the moment the ability is acquired
      // (only when it is newly gained, not when a branch re-lists it).
      if (!hadFortress && this.abilities.includes(FORTRESS_ABILITY_ID)) {
        this.currentHp += FORTRESS_HP_BONUS;
      }
    }

    if (branch.abilitiesRemoved && branch.abilitiesRemoved.length > 0) {
      this.abilities = this.abilities.filter(a => !branch.abilitiesRemoved!.includes(a));
    }

    // Grandmaster: the HP cap is enforced the moment the ability is held
    // after an upgrade — acquiring it adjusts the minion's HP down to 1
    // ("if the minion taking the grandmaster ability have more than 1 hp
    // its hp is adjusted to 1"), and a later +5 HP branch on an existing
    // Grandmaster is ignored the same way.
    if (this.hasGrandmaster() && this.currentHp > GRANDMASTER_MAX_HP) {
      this.currentHp = GRANDMASTER_MAX_HP;
    }
  }
}
